"use client";

import { useState, useEffect } from "react";

type FeatureFlags = Record<string, boolean>;

let cachedFlags: FeatureFlags | null = null;
let fetchPromise: Promise<FeatureFlags> | null = null;
let lastFetchTime = 0;
const CLIENT_CACHE_MS = 5 * 60 * 1000; // 5 minutes

async function fetchFlags(): Promise<FeatureFlags> {
  const now = Date.now();
  // Return cached if still fresh
  if (cachedFlags && now - lastFetchTime < CLIENT_CACHE_MS) {
    return cachedFlags;
  }
  // Deduplicate in-flight requests
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch("/api/feature-flags")
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch feature flags");
      return res.json();
    })
    .then((data) => {
      cachedFlags = data.flags ?? {};
      lastFetchTime = Date.now();
      fetchPromise = null;
      return cachedFlags!;
    })
    .catch((err) => {
      console.error("Error fetching feature flags:", err);
      fetchPromise = null;
      return cachedFlags ?? {};
    });

  return fetchPromise;
}

/**
 * Hook to access feature flags.
 *
 * Returns `{ flags, isLoading }`.
 * - `flags` is a `Record<string, boolean>` — access a flag via `flags.copyTrade`.
 * - Unknown flags return `undefined` (falsy), so unrecognized flags are off by default.
 * - Results are cached in-memory for 5 minutes across all component instances.
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags ?? {});
  const [isLoading, setIsLoading] = useState(!cachedFlags);

  useEffect(() => {
    let cancelled = false;
    fetchFlags().then((result) => {
      if (!cancelled) {
        setFlags(result);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { flags, isLoading };
}

/**
 * Helper to check a single flag value.
 * Returns `false` while loading or if the flag doesn't exist.
 */
export function useFeatureFlag(name: string): boolean {
  const { flags } = useFeatureFlags();
  return flags[name] ?? false;
}
